import Intercom from "@/crossPlatform/Intercom";
import { toMs } from "@/utils/date.util";
import { errorContainsCode } from "@/utils/error.util";
import { createTimeoutPromise } from "@/utils/shared.utils";

type RunIdentifyParams = {
  email: string;
  ensureTokenIsSet: () => Promise<void>;
  externalId: string;
  timeoutMs?: number;
};

// Default timeout of 10s for the entire identify flow,
// which includes waiting for the token to be set and up to two attempts
// to call Intercom.loginUserWithUserAttributes (if we encounter the
// known native race condition, code 102). This timeout is a safeguard to prevent hanging the app if something goes wrong
const DefaultIdentifyTimeoutMs = toMs({ seconds: 10 });

const runIdentifyWithTimeout = ({
  email,
  ensureTokenIsSet,
  externalId,
  timeoutMs = DefaultIdentifyTimeoutMs,
}: RunIdentifyParams) =>
  Promise.race([
    (async () => {
      // Ensure server-issued JWT (if IDV is enabled) is set before attempting
      // to identify the user with Intercom. `setUserJwt` may resolve in JS
      // before native has finished work, so callers should expect slight
      // timing gaps — see retry/delay below.
      await ensureTokenIsSet();

      // If the native Intercom SDK already reports a logged-in user, skip
      // re-identification to avoid redundant calls that can surface timing
      // issues on some platforms.
      const isIntercomLoggedin = await Intercom.isUserLoggedIn();
      if (isIntercomLoggedin) return;

      // Attempt login, retry once on Intercom code 102 (race condition)
      try {
        await Intercom.loginUserWithUserAttributes({
          email,
          userId: externalId,
        });
      } catch (caughtErr) {
        const isCode102 = errorContainsCode({ code: "102", err: caughtErr });
        // If we detect the known native race (code 102), wait briefly and
        // retry once. Keep this retry conservative — a single retry with a
        // short backoff is enough to avoid most intermittent races without
        // creating noisy duplicate identification calls.
        if (isCode102) {
          await createTimeoutPromise({
            resolveValue: undefined,
            shouldReject: false,
            timeoutMs: 500,
          });
          await Intercom.loginUserWithUserAttributes({
            email,
            userId: externalId,
          });
        } else {
          throw caughtErr;
        }
      }
    })(),
    createTimeoutPromise({
      resolveValue: undefined,
      shouldReject: true,
      timeoutMs,
    }),
  ]);

export default runIdentifyWithTimeout;
